import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantChangeService, NotificationService, OdinFeatures, StaticHelpers, } from '@odin/odin-core';
import {
  BasketDetails,
  ICardInfo,
  IFormattedTransaction,
  IOrderTracking,
  ITechnicalInfo,
  ITransaction,
  ITransactionDetail,
  ITransactionEvent,
  TransactionEventType,
  TransactionRefund,
  TransactionStatus,
  transactionIcon,
  transactionIconTooltip,
  addressLines,
} from '../transactions-layout/models';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TransactionFormatterService, TransactionsService, } from '../transactions-layout/services';
import { AccessGuardService, RoleGuardService } from '@odin/odin-authentication';
import {
  RechargeModalData,
  RechargeTransactionModalComponent
} from '../transactions-layout/components/recharge-transaction-modal/recharge-transaction-modal.component';
import {
  RefundModalData,
  RefundTransactionModalComponent
} from '../transactions-layout/components/refund-transaction-modal/refund-transaction-modal.component';
import {
  ReverseModalData,
  ReverseTransactionModalComponent
} from '../transactions-layout/components/reverse-transaction-modal/reverse-transaction-modal.component';
import { TransactionRecharge } from '../transactions-layout/models/transaction-recharge';
import { MatDialog } from '@angular/material/dialog';
import { ReceiptDetail } from '../print-receipt/print-receipt.component';
import { printPrintableContent } from '../print-receipt/print-helper';
import { InvoiceDetail, InvoiceOptions } from '../print-invoice/print-invoice.component';
import {
  InvoiceModalData,
  InvoiceOptionsModalComponent
} from './components/invoice-options-modal/invoice-options-modal.component';
import { ReceiptSettingsService } from "@odin/odin-settings";
import {
  CompleteModalData,
  CompleteTransactionModalComponent
} from "../transactions-layout/components/complete-transaction-modal/complete-transaction-modal.component";
import { WebhooksMessageAttempt } from '../transactions-layout/models/WebhooksMessageAttempt';
import { CallbackTransactionModalComponent } from '../transactions-layout/components/callback-transaction-modal/callback-transaction-modal.component';
import { parseUserAgent } from './ua-parser';

export type StatusPillKind = 'success' | 'warning' | 'danger' | 'neutral';

export interface HeroChip {
  label: string;
  kind: StatusPillKind | 'primary';
}

@Component({
  standalone: false,
  selector: 'odin-transaction-detail-page',
  templateUrl: './transaction-detail-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './transaction-detail-page.component.css',
})
export class TransactionDetailPageComponent implements OnInit {
  public loading: boolean = true;
  public callbacksLoading: boolean = true;
  private transactionTraceId: string;
  public transactionType: string = 'Transaction';

  public transaction: WritableSignal<IFormattedTransaction> =
    signal<IFormattedTransaction>({} as IFormattedTransaction);
  public relatedTransactions: WritableSignal<IFormattedTransaction[]> = signal<
    IFormattedTransaction[]
  >([]);
  public timelineEvents: WritableSignal<ITransactionEvent[]> = signal<ITransactionEvent[]>([]);
  public orderTracking: WritableSignal<IOrderTracking | null> = signal<IOrderTracking | null>(null);
  public cardInfo: WritableSignal<ICardInfo | null> = signal<ICardInfo | null>(null);
  public technical: WritableSignal<ITechnicalInfo | null> = signal<ITechnicalInfo | null>(null);
  public orderStatusUpdating: boolean = false;

  public shelper = StaticHelpers;
  public skeletonRows = [0, 1, 2, 3];
  public skeletonRowsShort = [0, 1, 2];
  svixFeatureEnabled: boolean;
  callbackAttempts: WebhooksMessageAttempt[] = [];

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private accessService: AccessGuardService,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private transactionService: TransactionsService,
    private transactionFormatter: TransactionFormatterService,
    private merchantChangeService: MerchantChangeService,
    private receiptService: ReceiptSettingsService,
    private roleGuardService: RoleGuardService,
    @Inject('features') public featureHelper: OdinFeatures,
  ) {
  }

  ngOnInit(): void {
    this.merchantChangeService.onChange('transaction detail page', window.location.href, () => {
      // transactions not available on different buid, so back out to transactions
      this.router.navigate(['transactions']);
    });
    this.transactionTraceId = this.activatedRoute.snapshot.params['traceId'];
    this.refreshTransaction();
    if (this.canAccessWebhooks()) {
      this.getCallbackDetails();
    }
  }

  public refreshTransaction(): void {
    this.loading = true;
    this.relatedTransactions.set([]);
    this.transaction.set({} as IFormattedTransaction);
    this.fetchTransactionDetail();
  }

  private fetchTransactionDetail(): void {
    this.loading = true;
    // Get monekId from query params if present
    const monekId = this.activatedRoute.snapshot.queryParamMap.get('monekid') || undefined;
    this.transactionService
      .getTransactionDetail(this.transactionTraceId, monekId)
      .subscribe({
        next: (detail: ITransactionDetail) => {
          this.applyDetail(detail);
          this.loading = false;
        },
        error: (err: unknown) => {
          this.loading = false;
          this.router.navigate(['transactions']);
          this.notificationService.ErrorSmallDialog(
            'Error fetching transaction details for ' + this.transactionTraceId, err
          );
        }
      });
  }

  private applyDetail(detail: ITransactionDetail): void {
    this.transaction.set(this.transactionFormatter.formatTransaction(detail.transaction));
    this.transactionType = this.GetTransactionType(detail.transaction);
    this.setRelated(detail.relatedTransactions ?? []);
    this.orderTracking.set(detail.orderTracking ?? null);
    this.cardInfo.set(detail.cardInfo ?? null);
    this.technical.set(detail.technical ?? null);
    this.timelineEvents.set(
      detail.events && detail.events.length > 0
        ? [...detail.events].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        : this.synthesiseEvents(),
    );
  }

  private setRelated(fetchedRelatedTransactions: ITransaction[]): void {
    this.relatedTransactions.set(
      fetchedRelatedTransactions
        .map((t: ITransaction) => this.transactionFormatter.formatTransaction(t))
        .sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()),
    );

    this.relatedTransactions().forEach((t: IFormattedTransaction) => {
      if (t.transactionType !== 'refund') return;
      if (t.status === 'declined') return;
      this.transaction.update((value: IFormattedTransaction) => {
        if (value.amountRefunded == undefined) value.amountRefunded = 0;
        value.amountRefunded = value.amountRefunded + t.amount;
        return value;
      });
    });

    // remainingValue is derived, not accumulated — recompute it from the
    // final amountRefunded rather than trusting a running total that only
    // gets updated above when the refund also appears in
    // relatedTransactions. Some refunds only ever show up as a TX360
    // timeline event, in which case the loop above never runs and
    // remainingValue previously stayed stuck at the full original amount
    // even though amountRefunded (populated directly by the backend) was
    // already correct.
    this.transaction.update((value: IFormattedTransaction) => ({
      ...value,
      remainingValue: value.amount - (value.amountRefunded ?? 0),
    }));
  }

  /**
   * Fallback when TX360 has no events for this transaction (or the feature
   * isn't live for the merchant yet): synthesise the same timeline the page
   * used to build from the transaction and its relations.
   */
  private synthesiseEvents(): ITransactionEvent[] {
    const tx = this.transaction();
    const events: ITransactionEvent[] = [];

    events.push({
      eventType: TransactionEventType.Authorisation,
      outcome: this.isSuccess(tx) ? 'success' : 'failure',
      title: this.isCompletedPreAuth() ? 'Completed Pre-Auth' : tx.isPreAuth ?
        (!this.isSuccess(tx) ? 'Pre-Auth Declined' : 'Pre-Authorised') :
        (!this.isSuccess(tx) ? 'Declined ' : 'Completed ') + tx.formattedType,
      timestamp: String(tx.createdOn),
    });

    for (const related of this.relatedTransactions()) {
      let title = related.formattedType;
      if (this.isCompletedPreAuth() || tx.isPreAuth &&
        (related.transactTransactionType && related.transactTransactionType.toUpperCase().startsWith('PAYMENT_ONLY_'))) {
        title = 'Completed Pre-Auth';
      }
      events.push({
        eventType: related.transactionType === 'refund'
          ? TransactionEventType.Refund
          : related.isReversal
            ? TransactionEventType.Reversal
            : TransactionEventType.Authorisation,
        outcome: this.isSuccess(related, true) ? 'success' : 'failure',
        title,
        timestamp: String(related.createdOn),
      });
    }

    return events.sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
  }

  public GetTransactionType(transaction: ITransaction): string {
    return transaction.transactionType === 'refund'
      ? 'Refund'
      : transaction.transactionType === 'reversal' ?
        'Reversal'
        : transaction.isReversed
          ? 'Reversed Transaction'
          : 'Transaction';
  }

  public isSuccess(
    transaction: ITransaction,
    reverseIsSuccess: boolean = false,
  ): boolean {
    if (transaction.status === TransactionStatus.Succeeded) return true;
    if (transaction.status === TransactionStatus.PreAuthSuccess) return true;
    if (reverseIsSuccess && transaction.status === TransactionStatus.Reversed)
      return true;
    return false;
  }

  public isCompletedPreAuth(): boolean {
    if (this.relatedTransactions().length > 0) {
      const _related = this.relatedTransactions();
      return _related.findIndex(r => r.status === TransactionStatus.PreAuthSuccess) > -1;
    }
    return false;
  }

  public transactionIcon(transaction: ITransaction): IconProp {
    return transactionIcon(transaction);
  }

  public transactionIconTooltip(transaction: IFormattedTransaction): string {
    return transactionIconTooltip(transaction);
  }

  // ** header status + hero chips **

  public statusPillKind(): StatusPillKind {
    const tx = this.transaction();
    if (tx.isReversed || tx.status === TransactionStatus.Reversed) return 'neutral';
    if (tx.status === TransactionStatus.PreAuthSuccess) return 'warning';
    if (this.isSuccess(tx)) {
      // A succeeded sale can still have been (partially) refunded since —
      // match the colouring already used for the "Fully/Partially
      // refunded" hero chip instead of falling through to a plain grey pill.
      const refunded = tx.amountRefunded ?? 0;
      if (refunded > 0) return (tx.remainingValue ?? 0) <= 0 ? 'danger' : 'warning';
      return 'success';
    }
    if (tx.status === TransactionStatus.Cancelled || tx.status === TransactionStatus.PreAuthFailed) return 'danger';
    return 'neutral';
  }

  public statusPillLabel(): string {
    const tx = this.transaction();
    if (tx.status === TransactionStatus.PreAuthSuccess) {
      return this.isCompletedPreAuth() || this.RelatedHasCompletedSale() ? 'Captured' : 'Pre-authorised';
    }
    return tx.formattedStatus;
  }

  public heroChips(): HeroChip[] {
    const tx = this.transaction();
    const chips: HeroChip[] = [];

    if (this.isUncapturedPreAuth()) chips.push({ label: 'Uncaptured', kind: 'warning' });

    const refunded = tx.amountRefunded ?? 0;
    if (refunded > 0) {
      chips.push(
        (tx.remainingValue ?? 0) <= 0
          ? { label: 'Fully refunded', kind: 'danger' }
          : { label: 'Partially refunded', kind: 'warning' },
      );
    }

    return chips;
  }

  // ** pre-auth capture **

  public isUncapturedPreAuth(): boolean {
    const tx = this.transaction();
    if (!tx.isPreAuth) return false;
    if (tx.status !== TransactionStatus.PreAuthSuccess) return false;
    if (tx.isReversed) return false;
    return !this.RelatedHasCompletedSale();
  }

  // ** customer block **

  public hasCustomerDetails(): boolean {
    const details = this.transaction().cardHolderDetails;
    if (!details) return false;
    return !!(details.name || details.address || details.postcode || details.phoneNumber || details.emailAddress)
      || !!this.billingAddress() || !!this.deliveryAddress();
  }

  /** billing address as one line per part, or empty when none was supplied */
  public billingAddress(): string {
    return addressLines(this.transaction().billingAddress).join('\n');
  }

  /** delivery address as one line per part, or empty when none was supplied */
  public deliveryAddress(): string {
    return addressLines(this.transaction().deliveryAddress).join('\n');
  }

  public customerAddress(): string {
    const details = this.transaction().cardHolderDetails;
    return [details?.address, details?.postcode].filter((part) => !!part).join('\n');
  }

  // ** payment / merchant data **

  public showPaymentReference(): boolean {
    const reference = this.transaction().paymentReference;
    return !this.shelper.isNullOrEmpty(reference) && !this.shelper.isGuid(reference);
  }

  public showMerchantData(): boolean {
    const detail = this.transaction().paymentDetail;
    return !this.shelper.isNullOrEmpty(detail) && !this.shelper.isGuid(detail);
  }

  public hasBasketData(): boolean {
    const basket = this.transaction().basket;
    if (!basket) return false;
    return (basket.basketItems || []).filter((item) => !!item).length > 0;
  }

  /** basket to render, or null when absent/empty/feature-gated */
  public displayBasket(): BasketDetails | null {
    if (!this.featureHelper['extended-transactions']) return null;
    if (!this.hasBasketData()) return null;
    return this.transaction().basket ?? null;
  }

  // ** technical **

  public ipAddress(): string | undefined {
    return this.technical()?.ipAddress || this.transaction().cardHolderDetails?.iPv4Address;
  }

  public userAgent(): string | undefined {
    return this.technical()?.userAgent;
  }

  public userAgentSummary(): string {
    return parseUserAgent(this.userAgent() || '');
  }

  public terminalId(): string | undefined {
    return this.technical()?.terminalId || this.transaction().transactionResult?.terminalId;
  }

  public cardProductLabel(info: ICardInfo): string {
    return [info.fundingType, info.product].filter((part) => !!part).join(' · ');
  }

  public formattedCardNumber(masked: string | undefined): string {
    if (!masked) return '-';
    const withDots = masked.replace(/\*/g, '•');
    return withDots.match(/.{1,4}/g)?.join(' ') || withDots;
  }

  // E.164 calling codes that are 1 or 3 digits long; everything else
  // defaults to 2 (the most common length, e.g. 44, 33, 49, 34, 39...).
  private static readonly ONE_DIGIT_CALLING_CODES = ['1', '7'];
  private static readonly THREE_DIGIT_CALLING_CODES = [
    '210', '211', '212', '213', '216', '218', '220', '221', '222', '223', '224', '225', '226',
    '227', '228', '229', '230', '231', '232', '233', '234', '235', '236', '237', '238', '239',
    '240', '241', '242', '243', '244', '245', '246', '247', '248', '249', '250', '251', '252',
    '253', '254', '255', '256', '257', '258', '260', '261', '262', '263', '264', '265', '266',
    '267', '268', '269', '290', '291', '297', '298', '299', '350', '351', '352', '353', '354',
    '355', '356', '357', '358', '359', '370', '371', '372', '373', '374', '375', '376', '377',
    '378', '379', '380', '381', '382', '383', '385', '386', '387', '389', '420', '421', '423',
    '500', '501', '502', '503', '504', '505', '506', '507', '508', '509', '590', '591', '592',
    '593', '594', '595', '596', '597', '598', '599', '670', '672', '673', '674', '675', '676',
    '677', '678', '679', '680', '681', '682', '683', '685', '686', '687', '688', '689', '690',
    '691', '692', '850', '852', '853', '855', '856', '870', '880', '886', '960', '961', '962',
    '963', '964', '965', '966', '967', '968', '970', '971', '972', '973', '974', '975', '976',
    '977', '992', '993', '994', '995', '996', '998',
  ];

  private callingCodeLength(digits: string): number {
    if (TransactionDetailPageComponent.THREE_DIGIT_CALLING_CODES.includes(digits.slice(0, 3))) return 3;
    if (TransactionDetailPageComponent.ONE_DIGIT_CALLING_CODES.includes(digits.slice(0, 1))) return 1;
    return 2;
  }

  public formattedPhoneNumber(phone: string | undefined): string {
    if (!phone) return '';
    const hasPlus = phone.trim().startsWith('+');
    const digits = phone.replace(/\D/g, '');

    if (!hasPlus) {
      return digits.match(/.{1,4}/g)?.join(' ') || digits;
    }

    // International: split off the calling code, then group the national
    // number like a phone number (e.g. +44 7890 123456) rather than
    // blindly chunking the whole string into 4s.
    const countryLength = this.callingCodeLength(digits);
    const country = digits.slice(0, countryLength);
    const rest = digits.slice(countryLength);
    const nsnFirst = rest.slice(0, 4);
    const nsnRest = rest.slice(4);
    return ['+' + country, nsnFirst, nsnRest].filter((part) => !!part).join(' ');
  }

  public phoneLink(phone: string | undefined): string {
    if (!phone) return '';
    const hasPlus = phone.trim().startsWith('+');
    const digits = phone.replace(/\D/g, '');
    return 'tel:' + (hasPlus ? '+' : '') + digits;
  }

  public showRefundAmount(): boolean {
    const amountRefunded = this.transaction().amountRefunded;
    if (amountRefunded === undefined) return false;
    if (amountRefunded <= 0) return false;
    return true;
  }

  public copyToClipboard(value: string): void {
    navigator.clipboard?.writeText(value).then(() => {
      this.notificationService.SmallDialog('Copied to clipboard', 'Close', 2000);
    });
  }

  // ** order tracking **

  public onOrderStatusSelected(status: string): void {
    this.orderStatusUpdating = true;
    const monekId = this.transaction().merchant?.monekId || undefined;
    this.transactionService
      .updateOrderStatus(this.transactionTraceId, status, monekId)
      .subscribe({
        next: (tracking: IOrderTracking) => {
          this.orderTracking.set(tracking);
          this.orderStatusUpdating = false;
          this.notificationService.SmallDialog(`Order marked "${status}"`, 'Close', 4000);
        },
        error: (err: unknown) => {
          this.orderStatusUpdating = false;
          this.notificationService.ErrorSmallDialog('Failed to update order status', err);
        },
      });
  }

  public canPrint(): boolean {
    if (this.transaction().status === TransactionStatus.Cancelled)
      return false;
    return true;
  }

  // ** Webhooks **
  public canAccessWebhooks(): boolean {
    const hasBuidFeature = this.accessService.buidHasFeature('svix');
    const hasPermission = this.accessService.hasNewGrant('int:Manage') || this.roleGuardService.hasPermission('settings:Webhooks');

    this.svixFeatureEnabled = hasBuidFeature && hasPermission;
    return this.svixFeatureEnabled;
  }

  public startResendCallback(): void {
    this.loading = true;
    this.transactionService
      .resendTransactionCallback(this.transactionTraceId)
      .subscribe(() => {
        this.notificationService.SmallDialog(
          'Transaction callback sent successfully',
        );
        this.loading = false;
      }, (err: unknown) => {
        this.notificationService.ErrorSmallDialog(
          'Failed to send transaction callback', err,
        );
        this.loading = false;
      });
  }

  public getCallbackDetails(): void {
    this.callbacksLoading = true;
    this.transactionService
      .getTransactionCallback(this.transactionTraceId)
      .subscribe({
        next: (response) => {
          if (Array.isArray(response)) {
            this.callbackAttempts = response;
          } else if (response) {
            this.callbackAttempts = [response];
          } else {
            this.callbackAttempts = [];
          }
          this.callbacksLoading = false;
        },
        error: (err: unknown) => {
          if (!(err instanceof HttpErrorResponse && err.status === 404)) {
            this.notificationService.ErrorSmallDialog('Failed to load callback details', err);
          }
          this.callbackAttempts = [];
          this.callbacksLoading = false;
        },
      });
  }

  public callbackDialogBox(attempt: WebhooksMessageAttempt): void {
    this.dialog.open(CallbackTransactionModalComponent, {
      width: '600px',
      data: attempt
    });
  }

  public isTransactDay(): boolean {
    // check happened TODAY before 23:59pm
    const transactionTime = new Date(this.transaction().createdOn);
    const thisMorning = new Date(Date.now());
    thisMorning.setHours(0);
    thisMorning.setMinutes(0);
    thisMorning.setSeconds(0);
    thisMorning.setMilliseconds(0);
    const tomorrow = new Date(thisMorning);
    tomorrow.setHours(23);
    tomorrow.setMinutes(59);
    tomorrow.setSeconds(59);
    if (transactionTime > thisMorning && transactionTime < tomorrow)
      return false;
    return true;
  }

  public isTransactWeek(): boolean {
    const transactionTime = new Date(this.transaction().createdOn);
    const now = new Date();

    const diffInMs = now.getTime() - transactionTime.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return diffInDays <= 7;
  }

  public RelatedHasCompletedSale(): boolean {
    let hasCompleteRelatedTransaction = false;
    this.relatedTransactions().forEach((transaction: IFormattedTransaction) => {
      // ensure transaction is after pre-auth (technically impossible)
      if (new Date(transaction.createdOn).getTime() > new Date(this.transaction().createdOn).getTime()) {
        if (transaction.status === TransactionStatus.Succeeded) hasCompleteRelatedTransaction = true;
      }
    });
    return hasCompleteRelatedTransaction;
  }

  public canReverse(): boolean {
    if (!this.accessService.hasNewGrant('txn:Reverse') && !this.roleGuardService.hasPermission('txn:Refund'))
      return this.LogAndReturn('No txn:Reverse Grant', false);
    if (this.transaction().isReversed)
      return this.LogAndReturn('Is Reversed', false);
    if (this.transaction().isReversal)
      return this.LogAndReturn('Is Reversal', false);
    if (this.transaction().status === TransactionStatus.Cancelled)
      return this.LogAndReturn('Is Cancelled', false);
    if (this.isTransactDay())
      return this.LogAndReturn('Is Transact Day', false);
    if (this.transaction().isPreAuth)
      return this.LogAndReturn('Is PreAuth', false);
    return true;
  }

  public canRefund(): boolean {
    if (!this.accessService.hasNewGrant('txn:Refund') && !this.roleGuardService.hasPermission('txn:Refund'))
      return this.LogAndReturn('No txn:Refund Grant', false);
    if (this.transaction().transactionType === 'refund')
      return this.LogAndReturn('Is Refund', false);
    if (this.transaction().isReversed)
      return this.LogAndReturn('Is Reversed', false);
    if (this.transaction().isReversal)
      return this.LogAndReturn('Is Reversal', false);
    if (this.transaction().status === TransactionStatus.Cancelled)
      return this.LogAndReturn('Is Cancelled', false);
    if (this.transaction().isPreAuth)
      return this.LogAndReturn('Is PreAuth', false);
    return true;
  }

  public canReCharge(): boolean {
    if (!this.accessService.hasNewGrant('txn:Recharge') && !this.roleGuardService.hasPermission('txn:Create'))
      return this.LogAndReturn('No txn:Recharge Grant', false);
    if (this.transaction().transactionType === 'refund')
      return this.LogAndReturn('Is Refund', false);
    if (this.transaction().isReversed)
      return this.LogAndReturn('Is Reversed', false);
    if (this.transaction().isReversal)
      return this.LogAndReturn('Is Reversal', false);
    if (this.transaction().status === TransactionStatus.Cancelled)
      return this.LogAndReturn('Is Cancelled', false);
    return true;
  }

  public canComplete(): boolean {
    if (!this.featureHelper['txn-complete'])
      return this.LogAndReturn('txn-complete feature disabled', false);
    if (!this.accessService.hasNewGrant('txn:Create') && !this.roleGuardService.hasPermission('txn:Create'))
      return this.LogAndReturn('No txn:Create Grant', false);
    if (!this.transaction().isPreAuth)
      return this.LogAndReturn('Isnt PreAuth', false);
    if (this.transaction().status === TransactionStatus.PreAuthFailed)
      return this.LogAndReturn('Invalid Preauth (failed)', false);
    if (!this.isTransactWeek())
      return this.LogAndReturn('Isnt within a week', false);
    if (this.RelatedHasCompletedSale())
      return this.LogAndReturn('Preauth is already complete', false);
    return true;
  }

  public OpenRelation(_transaction: IFormattedTransaction): void {
    const monekId = _transaction.merchant?.monekId;

    this.router.navigate(
      [`transactions/detail/${_transaction.crossReference}`],
      monekId ? { queryParams: { monekid: monekId } } : undefined
    );
  }

  public BackButtonHandle(): void {
    this.router.navigate(['transactions']);
  }

  //

  public getStackTrace(): string {
    try {
      throw new Error('');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return error.stack || '';
    }
  }

  public LogAndReturn(
    msg: string,
    toReturn: boolean,
    logLevel: number = 0,
  ): boolean {
    if (window._audit_logs == undefined) window._audit_logs = [];
    window._audit_logs.push({
      level: logLevel,
      log: msg,
      stack: this.getStackTrace(),
    });
    return toReturn;
  }

  public createReceipt(): ReceiptDetail {
    const tx = this.transaction();
    return new ReceiptDetail(
      '',
      tx.crossReference,
      tx.cardHolderDetails !== undefined ? tx.cardHolderDetails.name || tx.maskedCard : '',
      tx.paymentDetail,
      tx.createdOn,
      '',
      tx.amount,
      tx.currencyCountry,
      this.GetTransactionType(tx),
      tx.maskedCard,
      tx.paymentReference,
      tx.operatorName,
      tx.amountRefunded,
      tx.remainingValue,
      tx.status ? tx.status.toString() : '',
      tx.basket,
      tx.cardHolderDetails?.postcode,
      tx.cardHolderDetails?.emailAddress,
      tx.deliveryAddress,
      tx.billingAddress
    );
  }

  // handlers

  public invoiceDetail: WritableSignal<InvoiceDetail | null> = signal<InvoiceDetail | null>(null);

  public printReceipt(): void {
    this.invoiceDetail.set(null);
    printPrintableContent();
  }

  public openInvoiceDialog(): void {
    const invoiceModal = this.dialog.open(InvoiceOptionsModalComponent, {
      data: new InvoiceModalData(this.transaction()),
      autoFocus: false,
      panelClass: ['main-panel', 'rounded-dialog'],
      width: '560px',
      maxWidth: '95vw',
    });
    invoiceModal.afterClosed().subscribe((options: InvoiceOptions | null) => {
      if (options == null) return;
      this.invoiceDetail.set(new InvoiceDetail(this.createReceipt(), options));
      printPrintableContent();
    });
  }

  public resendReceipt(): void {
    this.receiptService.resendPaymentConfirmation(this.transaction().crossReference).subscribe(() => {
      this.notificationService.SmallDialog('Receipt sent successfully');
    });
  }

  public startRefundRequest(): void {
    const refundRequestModal = this.dialog.open(
      RefundTransactionModalComponent,
      {
        data: new RefundModalData(this.transaction()),
        autoFocus: false,
        panelClass: ['main-panel', 'rounded-dialog'],
        maxWidth: 520,
      },
    );
    refundRequestModal.afterClosed().subscribe((refund: TransactionRefund) => {
      if (refund == null) return;
      this.refreshTransaction();
    });
  }

  public startRechargeRequest(): void {
    const rechargeRequestModal = this.dialog.open(
      RechargeTransactionModalComponent,
      {
        data: new RechargeModalData(this.transaction()),
        autoFocus: false,
        panelClass: ['main-panel', 'rounded-dialog'],
        maxWidth: 520,
      },
    );
    rechargeRequestModal
      .afterClosed()
      .subscribe((recharge: TransactionRecharge) => {
        if (recharge == null) return;
        this.refreshTransaction();
      });
  }

  public startPreAuthCompleteRequest(): void {
    const completeRequestModal = this.dialog.open(
      CompleteTransactionModalComponent,
      {
        data: new CompleteModalData(this.transaction()),
        autoFocus: false,
        panelClass: 'main-panel',
        maxWidth: 400,
      },
    );
    completeRequestModal.afterClosed().subscribe(() => {
      setTimeout(() => {
        this.refreshTransaction();
      }, 500);
    });
  }

  public startReverseRequest(): void {
    const reverseRequestModal = this.dialog.open(
      ReverseTransactionModalComponent,
      {
        data: new ReverseModalData(this.transaction()),
        autoFocus: false,
        panelClass: 'main-panel',
        maxWidth: 400,
      },
    );
    reverseRequestModal.afterClosed().subscribe(() => {
      this.refreshTransaction();
    });
  }
}

